import React, { memo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSelectedDispositions } from "../store/slices/filtersSlice";

const StatCard = memo(({ label, count, percentage, isLoading, flag, onClick }) => {
  return (
    <div className="col">
      <div
        className="card text-white border-0 h-100"
        onClick={onClick}
        style={{
          backgroundColor: "var(--bg-elevated)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          cursor: onClick ? "pointer" : "default",
        }}
      >
        <div className="card-body p-3">
          <div className="small d-flex justify-content-between" style={{ color: "var(--text-secondary)" }}>
            <span>{label}</span>
            {flag && (
              <span
                className="badge rounded-pill ms-2"
                style={{
                  fontSize: "0.65rem",
                  backgroundColor: "var(--danger)",
                }}
              >
                ⚠
              </span>
            )}
          </div>

          {isLoading ? (
            <div
              className="spinner-border spinner-border-sm mt-3"
              role="status"
              style={{ color: "var(--accent)" }}
            />
          ) : (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <h5 className="mb-0" style={{ color: "var(--text-primary)" }}>{count ?? 0}</h5>
              <span
                className="badge rounded-pill"
                style={{
                  fontSize: "0.7rem",
                  backgroundColor: "var(--success-soft)",
                  color: "var(--success)",
                  minWidth: "45px",
                  textAlign: "center",
                }}
              >
                {percentage >= 0 ? "↑" : "↓"} {Math.abs(percentage || 0)}%
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

StatCard.displayName = "StatCard";

const statsConfig = [
  { key: "totalProcessed", label: "Total Processed" },
  { key: "Sales", label: "Sales" },
  { key: "Not Interested", label: "Not Interested" },
  { key: "Not Qualified", label: "Not Qualified" },
  { key: "DNC", label: "DNC" },
  { key: "Voicemail", label: "Voicemail", flagKey: "flagVoicemail" },
  { key: "Tech Issues", label: "Tech Issues" },
  { key: "DWSPI", label: "DWSPI" },
  { key: "Unresponsive", label: "Unresponsive" },
  { key: "Target hung up", label: "Hungup", flagKey: "flagTargetHU" },
  { key: "Dead Air", label: "Dead Air" },
  { key: "EnglishOnSpanishLine", label: "English on Spanish Line" },
  { key: "Callback", label: "Callback" },
  { key: "IVR", label: "IVR" },
  { key: "subsidy/incentivised", label: "Subsidy" },
  { key: "Language Barrier", label: "Language Barrier" },
  { key: "Misdialed", label: "Misdialed" },
  { key: "income", label: "income" },
  { key: "RejectedUnknown", label: "Rejected / Unknown", flagKey: "flagRejectedUnknown" },
  { key: "RedNoConnect", label: "Red No Connects", flagKey: "flagRedNoConnect" },
];

// Maps a card's key to the disposition value(s) to filter Call Logs by when
// clicked. "RejectedUnknown"/"RedNoConnect" are synthetic combined buckets
// (see statsService.js's _generateFlags) rather than a single disposition,
// so they map to multiple values. "income" and "EnglishOnSpanishLine" are
// not dispositions at all (they're QC sub-fields), so Call Logs has no way
// to filter by them yet - omitted here on purpose, not an oversight.
const DISPOSITION_FILTER_MAP = {
  Sales: ["Sales"],
  "Not Interested": ["Not Interested"],
  "Not Qualified": ["Not Qualified"],
  DNC: ["DNC"],
  Voicemail: ["Voicemail"],
  "Tech Issues": ["Tech Issues"],
  DWSPI: ["DWSPI"],
  Unresponsive: ["Unresponsive"],
  "Target hung up": ["Target hung up"],
  "Dead Air": ["Dead Air"],
  Callback: ["Callback"],
  IVR: ["IVR"],
  "subsidy/incentivised": ["subsidy/incentivised"],
  "Language Barrier": ["Language Barrier"],
  Misdialed: ["Misdialed"],
  RejectedUnknown: ["Failed", "Unknown", "Tech Issues"],
  RedNoConnect: ["Failed", "Target hung up", "Unresponsive", "IVR"],
};

const StatsCards = ({ stats: statsProp }) => {
  const stats = statsProp || {};
  const isLoading = !statsProp;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleCardClick = (config) => {
    if (isLoading) return;
    // "Total Processed" clears any disposition filter to show every call.
    const dispositions = config.key === "totalProcessed" ? [] : DISPOSITION_FILTER_MAP[config.key];
    if (!dispositions) return; // no filter mapping for this card yet (e.g. income, EnglishOnSpanishLine)

    dispatch(setSelectedDispositions(dispositions));
    navigate("/calls/ringba");
  };

  return (
    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5 g-2 mb-4 mt-1">
      {statsConfig.map((config) => {
        const statItem =
          config.key === "totalProcessed"
            ? {
                count: stats.totalCalls || 0,
                percentage: stats.totalCalls > 0 ? 100 : 0,
              }
            : config.key === "RejectedUnknown"
            ? {
                count: stats.flags?.rejectedUnknownCount || 0,
                percentage: stats.flags?.rejectedUnknownPct || 0,
              }
            : config.key === "RedNoConnect"
            ? {
                count: stats.flags?.redNoConnectCount || 0,
                percentage: stats.flags?.redNoConnectPct || 0,
              }
            : config.key === "EnglishOnSpanishLine"
            ? {
                count: stats.englishOnSpanishLineCount || 0,
                percentage: stats.englishOnSpanishLinePct || 0,
              }
            : stats.dispositions?.find(
                (d) => d.disposition.toLowerCase() === config.key.toLowerCase()
              );

        let flag = false;
        if (config.flagKey) {
          flag = !!stats.flags?.[config.flagKey];
        }

        const isClickable = config.key === "totalProcessed" || !!DISPOSITION_FILTER_MAP[config.key];

        return (
          <StatCard
            key={config.key}
            label={config.label}
            count={statItem?.count || 0}
            percentage={parseFloat(statItem?.percentage || 0)}
            isLoading={isLoading}
            flag={flag}
            onClick={isClickable ? () => handleCardClick(config) : undefined}
          />
        );
      })}
    </div>
  );
};

export default memo(StatsCards);
